// Payroll - Reports Tab (MoF Budget Office, Director General, Accountant General)
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';

const REPORT_TYPES = [
  { key: 'salary', label: '💰 Salary Report', color: '#059669', desc: 'Base salary, gross, deductions and net pay per personnel' },
  { key: 'summary', label: '📊 Payroll Summary', color: '#1b4332', desc: 'High-level financial breakdown by payroll group and period' },
  { key: 'department', label: '🏢 Dept Payroll', color: '#0284c7', desc: 'Departmental headcounts, base salaries, and net expenditures' },
  { key: 'operational_cost', label: '📊 Operational Costs', color: '#0f766e', desc: 'Detailed operational cost entries, funding sources, and support document status' },
  { key: 'budget', label: '🏛 Budget Utilization', color: '#92400e', desc: 'Operational cost budgets, financial year, and funding sources' },
  { key: 'rank', label: '🎖 Rank Salary Report', color: '#7c3aed', desc: 'Predefined rank salary grades and effective base rate history' },
  { key: 'salary_hold', label: '⏸ Salary Hold Report', color: '#ea580c', desc: 'Active and historical salary holds, reasons, and release timestamps' },
  { key: 'audit', label: '📋 Payroll Audit', color: '#374151', desc: 'Complete audit trail of creations, edits, approvals, and locks' },
  { key: 'allowance', label: '📈 Allowances', color: '#2563eb', desc: 'Breakdown of all approved allowances and bonuses applied' },
  { key: 'deduction', label: '📉 Deductions', color: '#dc2626', desc: 'Breakdown of all approved deductions applied to payroll' },
  { key: 'payment', label: '💳 Payment Transfer', color: '#4c1d95', desc: 'Final bank transfer payment execution summary' },
];

const ReportsTab = ({ api, user, armyTypeId, armyTypeName }) => {
  const [reportType, setReportType] = useState('salary');
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });

  const canAccess = hasAnyRole(user, ['admin', 'system_admin', 'payroll_officer', 'finance_officer', 'finance_director', 'director_general', 'accountant_general', 'auditor']);

  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await api.get('/departments');
        setDepartments(Array.isArray(res) ? res : (res?.departments || res?.data || []));
      } catch (e) { console.error(e); }
    };
    fetchDepts();
  }, []);

  if (!canAccess) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h3 style={{ color: '#dc2626' }}>Access Restricted</h3>
        <p style={{ color: '#6b7280' }}>Payroll reports are accessible to Finance Director, Director General, Accountant General, and Auditors only.</p>
      </div>
    );
  }

  const fetchReport = async () => {
    setLoading(true); setMsg({ text: '', type: '' }); setData(null);
    try {
      const params = new URLSearchParams({
        report_type: reportType,
        payroll_period: period,
        army_type_id: armyTypeId,
        ...(deptId ? { department_id: deptId } : {})
      });
      const res = await api.get(`/reports/payroll?${params}`);
      const reportData = res?.data || res;
      if (reportData?.data) {
        setData(reportData);
        setMsg({ text: `✅ ${reportData.data?.length || 0} records found for ${period}`, type: 'success' });
      } else {
        setMsg({ text: 'No data found for the selected filters', type: 'warning' });
      }
    } catch (e) {
      setMsg({ text: `Error generating report: ${e.message}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const downloadExport = (format) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        report_type: reportType,
        payroll_period: period,
        army_type_id: armyTypeId,
        format: format,
        auth_token: token || '',
        token: token || '',
        ...(deptId ? { department_id: deptId } : {})
      });
      window.open(`/api/v1/reports/payroll?${params}`, '_blank');
    } catch (e) { alert(`Error downloading ${format.toUpperCase()}`); }
  };

  const handlePrint = () => {
    window.print();
  };

  const activeType = REPORT_TYPES.find(r => r.key === reportType);

  const getColumns = () => {
    switch (reportType) {
      case 'salary':
      case 'master_payroll':
        return ['S/N', '(Pay Grade)', 'Name', 'Phone', 'Bank Name', 'Account Number', 'Ururka(Organization)', 'Salary', 'G.Xil', 'G.Farsamo', 'G.Hawl', 'Gross Salary', 'MM', 'Bolo', 'Health', 'Net Salary'];
      case 'summary': return ['Period', 'Group Name', 'Force Name', 'Headcount', 'Base Salary', 'Allowances', 'Deductions', 'Net Total', 'Status'];
      case 'department': return ['Department', 'Headcount', 'Base Salary', 'Allowances', 'Deductions', 'Net Salary'];
      case 'operational_cost': return ['Cost Name', 'Budget Code', 'Force', 'Department', 'Cost Type', 'Frequency', 'Amount', 'Financial Year', 'Funding Source', 'Status', 'Supporting Doc'];
      case 'rank': return ['Rank', 'Grade Name', 'Force', 'Base Salary', 'Effective From'];
      case 'salary_hold': return ['Army ID', 'Name', 'Rank', 'Force', 'Status', 'Held At', 'Reason', 'Released At'];
      case 'audit': return ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Details'];
      case 'budget': return ['Cost Name', 'Force', 'Department', 'Cost Type', 'Frequency', 'Amount', 'Budget Code', 'Financial Year', 'Funding Source', 'Status'];
      case 'allowance': return ['Army ID', 'Name', 'Rank', 'Allowance Name', 'Type', 'Amount Type', 'Configured', 'Calculated'];
      case 'deduction': return ['Army ID', 'Name', 'Rank', 'Deduction Name', 'Type', 'Amount Type', 'Configured', 'Calculated'];
      case 'payment': return ['Army ID', 'Rank', 'Name', 'Phone Number', 'Organization Unit', 'Bank Name', 'Account Number', 'Net Salary', 'Period', 'Status'];
      default: return [];
    }
  };

  const getRowValues = (row) => {
    switch (reportType) {
      case 'salary':
      case 'master_payroll':
        return [
          row.sn,
          row.pay_grade || row.rank || 'Grade-1',
          row.employee_name || row.name,
          row.phone || 'N/A',
          row.bank_name || 'Amal Bank',
          row.account_number || 'N/A',
          row.ururka || 'N/A',
          `$${parseFloat(row.salary || row.base_salary || 0).toFixed(2)}`,
          `$${parseFloat(row.g_xil || 0).toFixed(2)}`,
          `$${parseFloat(row.g_farsamo || 0).toFixed(2)}`,
          `$${parseFloat(row.g_hawl || 0).toFixed(2)}`,
          `$${parseFloat(row.gross_salary || 0).toFixed(2)}`,
          `$${parseFloat(row.mm || 0).toFixed(2)}`,
          `$${parseFloat(row.bolo || 0).toFixed(2)}`,
          `$${parseFloat(row.health || 0).toFixed(2)}`,
          `$${parseFloat(row.net_salary || 0).toFixed(2)}`
        ];
      case 'summary': return [row.period, row.group_name, row.force_name, row.headcount, `$${parseFloat(row.total_base || 0).toFixed(2)}`, `$${parseFloat(row.total_allowances || 0).toFixed(2)}`, `$${parseFloat(row.total_deductions || 0).toFixed(2)}`, `$${parseFloat(row.total_net || 0).toFixed(2)}`, row.status];
      case 'department': return [row.department, row.headcount, `$${parseFloat(row.base_salary || 0).toFixed(2)}`, `$${parseFloat(row.allowances || 0).toFixed(2)}`, `$${parseFloat(row.deductions || 0).toFixed(2)}`, `$${parseFloat(row.net_salary || 0).toFixed(2)}`];
      case 'operational_cost': return [row.cost_name, row.budget_code, row.force, row.department, row.cost_type, row.frequency, `$${parseFloat(row.amount || 0).toFixed(2)}`, row.financial_year, row.funding_source, row.status, row.supporting_document];
      case 'rank': return [row.rank, row.grade_name, row.force, `$${parseFloat(row.base_salary || 0).toFixed(2)}`, row.effective_from];
      case 'salary_hold': return [row.army_id, row.name, row.rank, row.force, row.status, row.held_at, row.reason, row.released_at];
      case 'audit': return [row.timestamp, row.user, row.action, row.resource_type, row.resource_id, typeof row.details === 'object' ? JSON.stringify(row.details) : row.details];
      case 'budget': return [row.cost_name, row.force, row.department, row.cost_type, row.frequency, `$${parseFloat(row.amount || 0).toFixed(2)}`, row.budget_code, row.financial_year, row.funding_source, row.status];
      case 'allowance': return [row.army_id, row.name, row.rank, row.allowance_name, row.allowance_type, row.amount_type, row.configured_amount, `$${parseFloat(row.calculated_amount || 0).toFixed(2)}`];
      case 'deduction': return [row.army_id, row.name, row.rank, row.deduction_name, row.deduction_type, row.amount_type, row.configured_amount, `$${parseFloat(row.calculated_amount || 0).toFixed(2)}`];
      case 'payment': return [row.army_id, row.rank || 'N/A', row.name, row.phone || 'N/A', row.organization_unit || 'N/A', row.bank_name || 'Bank', row.account_number || 'N/A', `$${parseFloat(row.net_salary || 0).toFixed(2)}`, row.payment_period, row.status];
      default: return [];
    }
  };

  const rows = data?.data || [];
  const msgBg = msg.type === 'error' ? '#fee2e2' : msg.type === 'warning' ? '#fffbeb' : '#dcfce7';
  const msgColor = msg.type === 'error' ? '#991b1b' : msg.type === 'warning' ? '#92400e' : '#166534';

  return (
    <div>
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
        <strong>📊 Payroll Reports Dashboard</strong> — Specialized reports for MoF Budget Office, Director General, Accountant General & Auditors.
        Export options include <strong>PDF/Print</strong>, <strong>Excel/CSV</strong>, and <strong>TSV</strong>.
      </div>

      {/* Report Type Selector */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 20 }}>
        {REPORT_TYPES.map(r => (
          <button key={r.key} onClick={() => { setReportType(r.key); setData(null); setMsg({ text: '', type: '' }); }}
            style={{
              padding: '10px 8px', border: `2px solid ${reportType === r.key ? r.color : '#e2e8f0'}`,
              background: reportType === r.key ? `${r.color}15` : '#fff',
              borderRadius: 8, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s'
            }}>
            <div style={{ fontSize: 16, marginBottom: 2 }}>{r.label.split(' ')[0]}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: reportType === r.key ? r.color : '#374151' }}>
              {r.label.split(' ').slice(1).join(' ')}
            </div>
          </button>
        ))}
      </div>

      {/* Description */}
      <div style={{ marginBottom: 16, padding: '8px 14px', background: `${activeType?.color}10`, border: `1px solid ${activeType?.color}30`, borderRadius: 6, fontSize: 13, color: activeType?.color, fontWeight: 600 }}>
        {activeType?.desc}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Payroll Period *</label>
            <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Force</label>
            <input readOnly value={armyTypeName || '—'} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#f8fafc', color: '#6b7280' }} />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Department (Optional)</label>
            <select value={deptId} onChange={e => setDeptId(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <button onClick={fetchReport} disabled={loading}
            style={{ padding: '9px 20px', background: loading ? '#94a3b8' : activeType?.color || '#1b4332', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13 }}>
            {loading ? '⏳ Generating...' : '🔍 Generate Report'}
          </button>
          {rows.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => downloadExport('pdf')}
                style={{ padding: '9px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                📕 PDF
              </button>
              <button onClick={() => downloadExport('excel')}
                style={{ padding: '9px 12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                📈 Excel
              </button>
              <button onClick={() => downloadExport('csv')}
                style={{ padding: '9px 12px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                📄 CSV
              </button>
              <button onClick={handlePrint}
                style={{ padding: '9px 12px', background: '#374151', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                🖨️ Print
              </button>
            </div>
          )}
        </div>
      </div>

      {msg.text && <div style={{ marginBottom: 16, padding: '10px 14px', background: msgBg, borderRadius: 6, color: msgColor, fontSize: 13, fontWeight: 600 }}>{msg.text}</div>}

      {/* Report Table */}
      {rows.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `${activeType?.color}08` }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: activeType?.color }}>
              {activeType?.label} — {armyTypeName} — {period}
            </h3>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{rows.length} records</span>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 500 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                <tr>
                  {getColumns().map(c => (
                    <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#374151', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    {getRowValues(row).map((val, j) => (
                      <td key={j} style={{ padding: '9px 14px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                        {val !== undefined && val !== null ? String(val) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !data && (
        <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Select a period and click Generate Report</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Reports pull live data from finalized payroll runs</div>
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
